import React from 'react';

import { Translate } from '@change-corgi/core/react/i18n';
import { ButtonLink } from '@change-corgi/design-system-legacy/components/actions';
import { Avatar } from '@change-corgi/design-system-legacy/components/media';
import { Flex } from '@change-corgi/design-system-legacy/layout';
import { useIsSunriseTheme } from '@change-corgi/design-system-legacy/theme';
import { Text } from '@change-corgi/design-system-legacy/typography';

import { usePetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';

import { usePetitionStarterMediaInquiry } from '../../hooks/petitionStarterMediaInquiry';
import { usePetitionStarterPhoto } from '../../hooks/petitionStarterPhoto';

import { StarterLink } from './components/StarterLink';

export function PetitionStarter(): React.JSX.Element {
	const {
		data: {
			petition: { id, organization, user },
		},
	} = usePetitionDetailsPageData();
	const { photoUrl } = usePetitionStarterPhoto();
	const {
		data: { enabled, url },
		actions: { handleMediaInquiryClick },
	} = usePetitionStarterMediaInquiry(id);
	const isSunriseThemeEnabled = useIsSunriseTheme();
	const AVATAR_SIZE = isSunriseThemeEnabled ? 80 : 64;

	return (
		<Flex
			variant="bordered"
			sx={{
				gap: isSunriseThemeEnabled ? 16 : 8,
				borderRadius: isSunriseThemeEnabled && '4px',
				borderColor: isSunriseThemeEnabled ? 'sunrise.semantic.stroke.weaker' : 'neutral-grey400',
				flexDirection: 'column',
			}}
			mb={16}
			mt={24}
			p={isSunriseThemeEnabled ? 24 : 16}
			data-testid="petition-starters-section"
			data-qa="petition-starter-section"
		>
			<Flex sx={{ gap: 16 }}>
				<Avatar variant="subtle" alt="avatar of the starter" src={photoUrl} size={AVATAR_SIZE} />
				<Flex
					sx={{
						flex: 1,
						// min-height + center so that 1 line is centered on the avatar
						// and 3+ lines are aligned with the avatar at the top
						minHeight: AVATAR_SIZE,
						flexDirection: 'column',
						alignItems: 'left',
						gap: 8,
					}}
				>
					<Flex sx={{ flexDirection: 'column' }}>
						<StarterLink user={user} organization={organization} />
						<Text
							as="p"
							sx={{
								...(isSunriseThemeEnabled && {
									color: 'sunrise.semantic.typography.weak',
								}),
							}}
							data-testid="petition-starter-text"
						>
							<Translate value="corgi.petition_gamma.details.starter_text" />
						</Text>
					</Flex>
				</Flex>
			</Flex>
			{isSunriseThemeEnabled && user.description && (
				<Text as="p" size="default" data-testid="petition-starter-description">
					{user.description}
				</Text>
			)}
			{enabled && (
				<ButtonLink
					data-testid="media-inquiry"
					data-qa="media-inquiry"
					to={url}
					variant="secondary"
					onClick={handleMediaInquiryClick}
				>
					<Translate value="corgi.petition_gamma.petition_starter.add_media_inquiries.action" />
				</ButtonLink>
			)}
		</Flex>
	);
}
